import { Stack, Typography } from "@mui/material";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { PaymentStatus } from "Components/Transactions/PaymentStatus";
import { QRPaymentSkeleton } from "Components/QRPayment/QRPaymentSkeleton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { QrCodePayment } from "Components/Shared/Payments/QrCodePayment";
import { Resources, useResource } from "Translations/Resources";
import { getLocalBankAccountNumberFromIBAN } from "Utils/AccountUtils";
import { getPaymentDescriptor } from "Utils/BankUtils";

type Props = {
  publicID: string;
  description?: string;
  // Lets a host dialog pause the fetch while closed without unmounting this
  // component (unmounting mid-close breaks the exit animation).
  isEnabled?: boolean;
};

export const InitiateQRPaymentComponent = ({
  publicID,
  description,
  isEnabled = true,
}: Props) => {
  const { data, isLoading, isError } = useTransactionsDetail({
    publicID,
    enabled: isEnabled,
  });
  const { t } = useResource();

  // No data + no error covers the disabled-while-closed state — show the
  // skeleton, not an error flash, when the dialog opens and the fetch starts.
  if (isLoading || (!data && !isError)) {
    return <QRPaymentSkeleton />;
  }

  if (!data) {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  const {
    amount,
    currencyCode,
    creditorIBAN,
    paymentReference,
    noteForDebtor,
    transactionStatus,
  } = data;

  const paymentDescriptor = getPaymentDescriptor(
    creditorIBAN,
    paymentReference || "",
    amount,
    currencyCode,
    noteForDebtor || "",
  );

  const accountNumber = getLocalBankAccountNumberFromIBAN(creditorIBAN);

  return (
    <Stack gap={6} textAlign="center">
      {description && <Typography fontSize="1rem">{description}</Typography>}
      <QrCodePayment
        iban={creditorIBAN}
        amount={amount}
        currencyCode={currencyCode}
        isLoading={isLoading}
        note={noteForDebtor || ""}
        paymentReference={paymentReference || ""}
        accountNumber={accountNumber}
        paymentDescriptor={paymentDescriptor}
        qrInstructions={`${t(Resources.Common.QRInstructions)} ${t(Resources.Transactions.BankTransferInfo)}`}
      />
      <PaymentStatus transactionStatus={transactionStatus} />
    </Stack>
  );
};
